/**
 * renomme-motion.js
 * Framer Motion (motion/mini) — loaded as ES module via theme.liquid.
 *
 * ARCHITECTURE:
 * - CSS never hides content. This file adds `motion-ready` to <html> AFTER
 *   the library loads, then immediately sets initial states programmatically.
 * - If this file fails to load / CDN unreachable, content stays fully visible.
 *
 * CDN: motion@11 mini build (~18 kB gzipped), no bundler required.
 */

import {
  animate,
  inView,
  scroll,
  stagger,
} from 'https://cdn.jsdelivr.net/npm/motion@11/+esm';

/* ─── Respect reduced motion ──────────────────────────────── */
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!reduced) {
  init();
}

function init() {
  /* Mark document as Motion-ready — CSS now hides [data-reveal] elements */
  document.documentElement.classList.add('motion-ready');

  /* Set initial hidden state on all reveals immediately (before paint)
     so there's no flash of visible content before animations start      */
  const allReveals = document.querySelectorAll('[data-reveal]');
  allReveals.forEach(el => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(28px)';
  });

  /* ══════════════════════════════════════════════════════════
     1. NAV — stagger links in on load
  ══════════════════════════════════════════════════════════ */
  const navLinks = document.querySelectorAll('.desktop-nav a, .wordmark');
  if (navLinks.length) {
    animate(
      navLinks,
      { opacity: [0, 1], y: [-10, 0] },
      { duration: 0.55, delay: stagger(0.08, { start: 0.2 }), easing: [0.16, 1, 0.3, 1] }
    );
  }

  /* ══════════════════════════════════════════════════════════
     2. HERO — cascade headline, lead, buttons, meta
  ══════════════════════════════════════════════════════════ */
  const heroReveal = document.querySelector('.hero [data-reveal]:first-child');
  if (heroReveal) {
    heroReveal.style.opacity = '0';
    heroReveal.style.transform = 'translateY(32px)';
    const kids = Array.from(heroReveal.children);
    if (kids.length) {
      kids.forEach(k => { k.style.opacity = '0'; k.style.transform = 'translateY(20px)'; });
      animate(
        kids,
        { opacity: [0, 1], y: [20, 0] },
        { duration: 0.7, delay: stagger(0.12, { start: 0.18 }), easing: [0.16, 1, 0.3, 1] }
      );
      animate(heroReveal, { opacity: [0, 1], y: [0, 0] }, { duration: 0.01 });
    } else {
      animate(heroReveal, { opacity: [0, 1], y: [32, 0] }, { duration: 0.7, delay: 0.18, easing: [0.16, 1, 0.3, 1] });
    }
  }

  /* ══════════════════════════════════════════════════════════
     3. SEAL — spring rise + ambient hover rotation
  ══════════════════════════════════════════════════════════ */
  const sealWrap = document.querySelector('.hero [data-reveal]:last-child');
  if (sealWrap) {
    sealWrap.style.opacity = '0';
    sealWrap.style.transform = 'translateY(0)';
    animate(sealWrap, { opacity: [0, 1] }, { duration: 0.8, delay: 0.55, easing: [0.16, 1, 0.3, 1] });
  }
  const seal = document.querySelector('.r-seal');
  if (seal) {
    animate(
      seal,
      { opacity: [0, 1], scale: [0.88, 1], rotate: [-6, 0] },
      { duration: 1.1, delay: 0.5, easing: [0.34, 1.56, 0.64, 1] }
    );
    seal.addEventListener('mouseenter', () => animate(seal, { rotate: 8 }, { duration: 1.8, easing: [0.16, 1, 0.3, 1] }));
    seal.addEventListener('mouseleave', () => animate(seal, { rotate: 0 }, { duration: 1.4, easing: [0.16, 1, 0.3, 1] }));
  }

  /* ══════════════════════════════════════════════════════════
     4. SCROLL REVEALS — all [data-reveal] outside hero
  ══════════════════════════════════════════════════════════ */
  allReveals.forEach(el => {
    if (el.closest('.hero')) return;
    inView(
      el,
      ({ target }) => {
        animate(target, { opacity: [0, 1], y: [28, 0] }, { duration: 0.75, easing: [0.16, 1, 0.3, 1] });
      },
      { amount: 0.12 }
    );
  });

  /* ══════════════════════════════════════════════════════════
     5. SERVICES CARDS — stagger
  ══════════════════════════════════════════════════════════ */
  const servicesGrid = document.querySelector('.grid--4');
  if (servicesGrid) {
    inView(
      servicesGrid,
      ({ target }) => {
        const cards = target.querySelectorAll('.card');
        animate(cards, { opacity: [0, 1], y: [28, 0] }, {
          duration: 0.65,
          delay: stagger(0.1),
          easing: [0.16, 1, 0.3, 1]
        });
      },
      { amount: 0.1 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     6. PRINCIPLES — stagger from left
  ══════════════════════════════════════════════════════════ */
  const principleSection = document.querySelector('#approach');
  if (principleSection) {
    inView(
      principleSection,
      ({ target }) => {
        const rows = target.querySelectorAll('.principle');
        if (rows.length) {
          animate(rows, { opacity: [0, 1], x: [-18, 0] }, {
            duration: 0.65,
            delay: stagger(0.1),
            easing: [0.16, 1, 0.3, 1]
          });
        }
      },
      { amount: 0.1 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     7. GOVERNANCE PANEL — slide from right + list cascade
  ══════════════════════════════════════════════════════════ */
  const govPanel = document.querySelector('.governance-panel');
  if (govPanel) {
    inView(
      govPanel,
      ({ target }) => {
        animate(target, { opacity: [0, 1], x: [24, 0] }, { duration: 0.75, delay: 0.15, easing: [0.16, 1, 0.3, 1] });
        const items = target.querySelectorAll('li');
        if (items.length) {
          animate(items, { opacity: [0, 1], x: [12, 0] }, {
            duration: 0.5,
            delay: stagger(0.07, { start: 0.35 }),
            easing: [0.16, 1, 0.3, 1]
          });
        }
      },
      { amount: 0.2 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     8. SCOPE LIST cells — fan in
  ══════════════════════════════════════════════════════════ */
  const scopeList = document.querySelector('.scope-list');
  if (scopeList) {
    inView(
      scopeList,
      ({ target }) => {
        const cells = target.querySelectorAll('div');
        animate(cells, { opacity: [0, 1], y: [16, 0] }, {
          duration: 0.55,
          delay: stagger(0.09),
          easing: [0.16, 1, 0.3, 1]
        });
      },
      { amount: 0.15 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     9. PRIVATE CTA — scale up
  ══════════════════════════════════════════════════════════ */
  const cta = document.querySelector('.private-cta');
  if (cta) {
    inView(
      cta,
      ({ target }) => {
        animate(target, { opacity: [0, 1], scale: [0.97, 1] }, { duration: 0.8, easing: [0.16, 1, 0.3, 1] });
      },
      { amount: 0.25 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     10. FOOTER — gentle rise
  ══════════════════════════════════════════════════════════ */
  const footer = document.querySelector('.site-footer');
  if (footer) {
    inView(
      footer,
      ({ target }) => {
        animate(target, { opacity: [0, 1], y: [20, 0] }, { duration: 0.7, easing: [0.16, 1, 0.3, 1] });
      },
      { amount: 0.1 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     11. CARD mouse-follow glow
  ══════════════════════════════════════════════════════════ */
  document.querySelectorAll('.card').forEach(card => {
    card.addEventListener('mousemove', e => {
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--mx', ((e.clientX - rect.left) / rect.width * 100) + '%');
      card.style.setProperty('--my', ((e.clientY - rect.top)  / rect.height * 100) + '%');
    });
  });

  /* ══════════════════════════════════════════════════════════
     12. HERO PARALLAX on scroll
  ══════════════════════════════════════════════════════════ */
  const heroSection = document.querySelector('.hero');
  if (heroSection) {
    scroll(
      animate(heroSection, { y: [0, 80] }),
      { target: heroSection, offset: ['start start', 'end start'] }
    );
  }

  /* ══════════════════════════════════════════════════════════
     13. PAGE HERO (inner pages)
  ══════════════════════════════════════════════════════════ */
  const pageHero = document.querySelector('.page-hero');
  if (pageHero) {
    const children = Array.from(pageHero.children);
    if (children.length) {
      children.forEach(c => { c.style.opacity = '0'; c.style.transform = 'translateY(24px)'; });
      animate(children, { opacity: [0, 1], y: [24, 0] }, {
        duration: 0.65,
        delay: stagger(0.1, { start: 0.1 }),
        easing: [0.16, 1, 0.3, 1]
      });
    }
  }

  /* ══════════════════════════════════════════════════════════
     14. APPROACH STEPS (inner page)
  ══════════════════════════════════════════════════════════ */
  const steps = document.querySelector('.steps');
  if (steps) {
    inView(
      steps,
      ({ target }) => {
        const stepEls = target.querySelectorAll('.step');
        animate(stepEls, { opacity: [0, 1], x: [-16, 0] }, {
          duration: 0.6,
          delay: stagger(0.12),
          easing: [0.16, 1, 0.3, 1]
        });
      },
      { amount: 0.08 }
    );
  }

  /* ══════════════════════════════════════════════════════════
     15. KICKER lines — slide in
  ══════════════════════════════════════════════════════════ */
  document.querySelectorAll('.kicker').forEach(kicker => {
    if (kicker.closest('.hero')) return;
    inView(
      kicker,
      ({ target }) => {
        animate(target, { opacity: [0, 1], x: [-20, 0] }, { duration: 0.6, easing: [0.16, 1, 0.3, 1] });
      },
      { amount: 0.5 }
    );
  });
}
